import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import cx from "ui/cx";
import type { FormFieldState } from "ui/forms/formFieldState";
import styles from "./Input.module.scss";

// https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
export type AutocompleteField =
  | "off"
  | "name"
  | "honorific-prefix"
  | "given-name"
  | "additional-name"
  | "family-name"
  | "honorific-suffix"
  | "nickname"
  | "username"
  | "new-password"
  | "current-password"
  | "one-time-code"
  | "organization-title"
  | "organization"
  | "street-address"
  | "address-line1"
  | "address-line2"
  | "address-line3"
  | "address-level4"
  | "address-level3"
  | "address-level2"
  | "address-level1"
  | "country"
  | "country-name"
  | "postal-code"
  | "cc-name"
  | "cc-given-name"
  | "cc-additional-name"
  | "cc-family-name"
  | "cc-number"
  | "cc-exp"
  | "cc-exp-month"
  | "cc-exp-year"
  | "cc-csc"
  | "cc-type"
  | "email"
  | "transaction-currency"
  | "transaction-amount"
  | "language"
  | "bday"
  | "bday-day"
  | "bday-month"
  | "bday-year"
  | "sex"
  | "tel"
  | "url"
  | "photo";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  inputState?: FormFieldState;
  inputSize?: "medium" | "mediumLarge" | "large";
  autoComplete?: AutocompleteField;
  noDisabledCursor?: boolean;
  validate?: (value?: React.InputHTMLAttributes<HTMLInputElement>["value"]) => string | null | undefined;
  autoFocus?: boolean;
  autoSelect?: boolean;
  transparent?: boolean;
  onCommit?: () => void;
  wrapperClassName?: string;
  ref?: React.Ref<InputRef>;
}

export interface InputRef extends Partial<HTMLInputElement> {
  value: string;
  validate: () => string;
  focus: () => void;
  blur: () => void;
  select: () => void;
  getBoundingClientRect: () => DOMRect;
  scrollIntoView: (arg?: boolean | ScrollIntoViewOptions) => void;
}

function InputComponent({
  value,
  type = "text",
  inputState = "success",
  inputSize = "large",
  onBlur,
  onKeyUp,
  onCommit,
  validate,
  title,
  disabled = false,
  noDisabledCursor = false,
  autoFocus = false,
  autoSelect = false,
  transparent = false,
  required,
  className,
  wrapperClassName,
  ref,
  ...props
}: InputProps) {
  const [error, setError] = useState<string>();
  const [touched, setTouched] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  // Allow parent components to access the input element and trigger validation.
  // Useful for forms where one input selection can affect another.
  useImperativeHandle(
    ref,
    () => {
      if (!inputRef.current) {
        throw new Error("Input ref not initialized");
      }
      return {
        get value() {
          return inputRef.current?.value ?? "";
        },
        set value(newValue: string) {
          if (inputRef.current) {
            inputRef.current.value = newValue;
          }
        },
        validate: () => {
          setTouched(true);
          const msg = validateInput();
          setError(msg);
          return msg;
        },
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        select: () => inputRef.current?.select(),
        getBoundingClientRect(): DOMRect {
          return inputRef.current?.getBoundingClientRect() ?? new DOMRect();
        },
        scrollIntoView: (arg) => inputRef.current?.scrollIntoView(arg),
      };
    },
    [inputRef, validate, setTouched, setError],
  );

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (autoSelect) {
      inputRef.current?.select();
    }
  }, [autoSelect]);

  function validateInput(): string {
    const messages: string[] = [];
    if (required && !value) {
      messages.push(title ? `${title} is required` : "Required");
    }

    if (typeof validate === "function") {
      const validationMessage = validate(value);
      if (validationMessage) {
        messages.push(validationMessage);
      }
    }

    return messages.join(", ");
  }

  const handleCommit = (): void => {
    setTouched(true);
    const msg = validateInput();

    if (!msg) {
      setTouched(false);
      setError(undefined);
    } else {
      setError(msg);
    }

    onCommit?.();
  };

  const inputEl = (
    <input
      value={value}
      ref={inputRef}
      type={type}
      disabled={disabled}
      title={disabled ? title : undefined}
      data-testid="input-element"
      {...props}
      className={cx(
        "input",
        styles.wrapper,
        { styles, noDisabledCursor, error, transparentInput: transparent },
        styles[inputState],
        styles[inputSize],
        className,
      )}
      onBlur={(e) => {
        onBlur?.(e);
        handleCommit();
      }}
      onKeyUp={(e) => {
        onKeyUp?.(e);

        // revalidated on every key press for immediate feedback to the user
        if (touched && error) {
          setError(undefined);
        }

        if (e.key === "Enter" && inputRef.current) {
          handleCommit();
        }
      }}
    />
  );

  return (
    <div className={cx(styles.validationWrapper, wrapperClassName)}>
      {inputEl}
      {error && (
        <span data-testid="input-validation-error" className={styles.validationError}>
          {error}
        </span>
      )}
    </div>
  );
}

export default function Input(props: InputProps) {
  return <InputComponent {...props} />;
}

export function ReturnCommitInput({ value: inputValue, ...props }: InputProps) {
  const [value, setValue] = useState(inputValue);
  useEffect(() => {
    setValue(inputValue);
  }, [inputValue]);
  return <InputComponent {...props} value={value} onChange={(e) => setValue(e.target.value)} />;
}
